import { useCallback, useDeferredValue, useEffect, useMemo, useState } from "react";
import { GridPaginationModel } from "@mui/x-data-grid";
import { Permission } from "../roles/types/permissions";
import { buildGroupedByApp, isSystemApp, CrudVerb, CRUD_VERBS, ModelGroup } from "../roles/permissionUtils";
import { ApiApplicationGrant, ExternalResource } from "./types";

export type VisibleApp = [string, ModelGroup[]];
export type GrantBulkAction = "readOnly" | "clearAll";

const emptyGrant = (appLabel: string, model: string): ApiApplicationGrant => ({
  app_label: appLabel,
  model,
  can_view: false,
  can_add: false,
  can_change: false,
  can_delete: false,
});

/**
 * Adapts the Roles app/model permission grouping
 * (permissionUtils.buildGroupedByApp) to drive a per-model CRUD grant picker
 * for API Applications. `/api/authentication/permissions/` is used purely to
 * enumerate app/model pairs - grants here are booleans keyed by
 * "app_label.model", not Django Permission ids.
 *
 * `resources` (GET /api/api-applications/resources/) marks which models the
 * external API actually serves; by default only those are shown.
 */
export function useApiApplicationGrantsPicker(
  allPermissions: Permission[],
  grants: Record<string, ApiApplicationGrant>,
  setGrants: React.Dispatch<React.SetStateAction<Record<string, ApiApplicationGrant>>>,
  resources: ExternalResource[] = [],
) {
  const [searchTerm, setSearchTerm] = useState("");
  const deferredSearch = useDeferredValue(searchTerm);
  const [expandedApps, setExpandedApps] = useState<Record<string, boolean>>({});
  const [showSystemApps, setShowSystemApps] = useState(false);
  const [showEventModels, setShowEventModels] = useState(false);
  const [showAllModels, setShowAllModels] = useState(false);
  const [pagination, setPagination] = useState<GridPaginationModel>({ page: 0, pageSize: 10 });

  const groupedByApp = useMemo(() => buildGroupedByApp(allPermissions), [allPermissions]);
  const resourceByKey = useMemo(
    () => Object.fromEntries(resources.map((r) => [r.key, r])) as Record<string, ExternalResource>,
    [resources],
  );
  const resourceFor = useCallback(
    (appLabel: string, model: string): ExternalResource | undefined => resourceByKey[`${appLabel}.${model}`],
    [resourceByKey],
  );

  const visibleApps = useMemo<VisibleApp[]>(() => {
    const term = deferredSearch.trim().toLowerCase();
    const apiOnly = !showAllModels && resources.length > 0;
    return Object.entries(groupedByApp)
      .filter(([app]) => showSystemApps || !isSystemApp(app))
      .map<VisibleApp>(([app, models]) => [
        app,
        models.filter((m) => {
          const isResource = !!resourceByKey[`${app}.${m.model}`];
          // BookingEvent ends in "event" and has a "booking" sibling, so the
          // pghistory-twin heuristic misfires on it; registry models are never twins.
          if (!isResource && !showEventModels && m.isEventTwin) return false;
          return !apiOnly || isResource;
        }),
      ])
      .filter(([app, models]) => {
        if (models.length === 0) return false;
        if (!term) return true;
        return (
          app.toLowerCase().includes(term) ||
          models.some((m) => m.model.toLowerCase().includes(term))
        );
      });
  }, [groupedByApp, showSystemApps, showEventModels, showAllModels, resources.length, resourceByKey, deferredSearch]);

  useEffect(() => {
    setPagination((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
  }, [deferredSearch, showSystemApps, showEventModels, showAllModels]);

  const pagedApps = useMemo(() => {
    const start = pagination.page * pagination.pageSize;
    return visibleApps.slice(start, start + pagination.pageSize);
  }, [visibleApps, pagination]);

  const toggleApp = useCallback((app: string) => {
    setExpandedApps((prev) => ({ ...prev, [app]: !prev[app] }));
  }, []);

  const toggleVerb = useCallback(
    (appLabel: string, model: string, verb: CrudVerb) => {
      const key = `${appLabel}.${model}`;
      setGrants((prev) => {
        const existing = prev[key] || emptyGrant(appLabel, model);
        const field = `can_${verb}` as const;
        return { ...prev, [key]: { ...existing, [field]: !existing[field] } };
      });
    },
    [setGrants],
  );

  /** Mirrors roles/usePermissionsPicker handleBulk, scoped to the visible apps. */
  const bulk = useCallback(
    (action: GrantBulkAction) => {
      setGrants((prev) => {
        const next = { ...prev };
        visibleApps.forEach(([app, models]) => {
          models.forEach((m) => {
            const key = `${app}.${m.model}`;
            if (action === "clearAll") {
              delete next[key];
            } else {
              next[key] = { ...emptyGrant(app, m.model), can_view: true };
            }
          });
        });
        return next;
      });
    },
    [visibleApps, setGrants],
  );

  const grantsList = useMemo(
    () => Object.values(grants).filter((g) => CRUD_VERBS.some((v) => g[`can_${v}` as const])),
    [grants],
  );

  return {
    searchTerm,
    setSearchTerm,
    expandedApps,
    toggleApp,
    showSystemApps,
    setShowSystemApps,
    showEventModels,
    setShowEventModels,
    showAllModels,
    setShowAllModels,
    visibleApps,
    pagedApps,
    pagination,
    setPagination,
    resourceFor,
    toggleVerb,
    bulk,
    grantsList,
  };
}
